import React, { useCallback, useEffect, useState } from 'react';
import { Divider, FormInstance, Typography } from 'antd';
import { IRequirement } from 'interfaces/requirements';
import { createRequirement, updateRequirement } from 'requests/requirements';
import {
  getRequirementWeeks,
  parseAvailabilityDate,
  toAvailabilityDate,
  weeksToMinutes
} from 'services/date';
import {
  RequirementKind,
  SelectRequirement
} from 'components/form-items/SelectRequirement';

interface Props {
  form: FormInstance;
  nodeId: string;
  classroomId: string;
  requirements: IRequirement[];
  onSaveReady: (saveHandler: () => Promise<void>) => void;
}

const RequirementContentModal: React.FC<Props> = ({
  form,
  nodeId,
  classroomId,
  requirements,
  onSaveReady
}) => {
  const [requirementKind, setRequirementKind] =
    useState<RequirementKind>('week');
  const [requirement, setRequirement] = useState<IRequirement | undefined>();

  useEffect(() => {
    const existingRequirement = requirements.find(
      item => item.resource_id === nodeId && item.resource_kind === 'tree_content'
    );

    if (existingRequirement?.rules?.length) {
      const rule = existingRequirement.rules[existingRequirement.rules.length - 1];

      if (rule.kind === 'date') {
        setRequirementKind('date');
        form.setFieldsValue({ duration: parseAvailabilityDate(rule.value) });
      } else {
        setRequirementKind('week');
        form.setFieldsValue({ duration: getRequirementWeeks(+rule.value) });
      }
    } else {
      setRequirement(undefined);
      setRequirementKind('week');
      form.setFieldsValue({ duration: 0 });
    }

    setRequirement(existingRequirement);
  }, [form, nodeId, requirements]);

  const saveRequirement = useCallback(async () => {
    const duration = form.getFieldValue('duration');

    if (duration === '' || duration === null || typeof duration === 'undefined') {
      return;
    }

    const kind = requirementKind === 'date' ? 'date' : 'duration_minutes';
    const selectedDuration =
      requirementKind === 'date'
        ? toAvailabilityDate(duration)
        : weeksToMinutes(duration);

    if (requirement?.id) {
      const response = await updateRequirement({
        requirementId: requirement.id,
        classroomId,
        nodeId,
        duration: selectedDuration,
        key: kind === 'date' ? 'current_date' : 'possession_date',
        kind,
        resourceType: 'tree_content'
      });

      setRequirement(response || requirement);
      return;
    }

    const response = await createRequirement({
      classroomId,
      nodeId,
      duration: selectedDuration,
      key: kind === 'date' ? 'current_date' : 'possession_date',
      kind,
      resourceType: 'tree_content'
    });

    setRequirement(response || requirement);
  }, [classroomId, form, nodeId, requirement, requirementKind]);

  useEffect(() => {
    onSaveReady(saveRequirement);
  }, [onSaveReady, saveRequirement]);

  return (
    <>
      <Typography.Title level={5} className="pb-2">
        Regra de liberação
      </Typography.Title>

      <SelectRequirement
        kind={requirementKind}
        defaultValue={0}
        onSelect={value => {
          setRequirementKind(value);
          form.setFieldsValue({ duration: undefined });
        }}
      />

      <Divider />
    </>
  );
};

export default RequirementContentModal;
